import { useState } from 'react';
import { Wallet } from 'lucide-react';
import type { Game, GameParticipant, Member, MemberPublic, PostMetadataGame } from '@/lib/db/types';
import { findGameFormat } from '@/lib/reference/golf-games';
import { venmoPay } from '@/lib/venmo';
import { Sheet } from '@/components/shared/Sheet';
import { Avatar } from '@/components/shared/Avatar';

interface Props {
  game?: Game;
  gameSnapshot?: PostMetadataGame;
  participants?: GameParticipant[];
  members: Record<string, MemberPublic>;
  courseName?: string;
  currentMember?: Member;
  onClose: () => void;
  onShare?: () => void;
  onDonate?: (amountCents: number) => void;
}

export function GameResultModal({
  game,
  gameSnapshot,
  participants = [],
  members,
  courseName,
  currentMember,
  onClose,
  onShare,
  onDonate,
}: Props) {
  const [showDonate, setShowDonate] = useState(false);
  const format = game ? findGameFormat(game.format_id) : null;
  const settlement = (game?.config?.settlement || gameSnapshot?.settlement) as Record<string, unknown> | undefined;
  const type = (settlement?.type as string) || gameSnapshot?.type || game?.format_id || 'nassau';
  const amount = (settlement?.amount as number) || gameSnapshot?.amount || (game ? game.wager_cents / 100 : 5);
  const scoring = (settlement?.scoring as string) || gameSnapshot?.scoring || 'net';
  const net = (settlement?.net as Record<string, number>) || {};
  const myKey = currentMember?.id || participants[0]?.member_id || '';
  const myNet = net[myKey] ?? 0;
  const winnings = Math.max(0, myNet);

  const opps = participants
    .filter((p) => p.member_id !== myKey)
    .map((p) => {
      const mid = p.member_id;
      if (mid && members[mid]) return members[mid];
      return { id: p.guest_name || '', name: p.guest_name || 'Guest', handle: '', venmo: null } as MemberPublic;
    });

  const donatePct = (pct: number) => {
    if (!onDonate) return;
    onDonate(Math.round(winnings * 100 * pct / 100));
    setShowDonate(false);
  };

  const typeLabel = type === 'skins' ? 'Skins' : type === 'nassau' ? 'Nassau' : type.charAt(0).toUpperCase() + type.slice(1);
  return (
    <Sheet title={`${typeLabel} Results`} onClose={onClose}>
      <div className="space-y-4 p-4 pb-8">
        <div className="rounded-xl border border-gold/40 bg-navy p-5 text-center text-cream">
          <div className="inline-block rounded bg-gold px-2.5 py-1 text-[10px] font-extrabold tracking-wide text-navy">
            {type.toUpperCase()} · {scoring.toUpperCase()}
          </div>
          <div className="mt-2 font-serif text-lg font-bold">{courseName || game?.name || format?.name}</div>
          <div className="text-xs text-gold">${amount} {type === 'nassau' ? 'per leg' : type === 'skins' ? 'per hole' : ''}</div>
        </div>

        {Array.isArray(settlement?.board) && type !== 'nassau' && type !== 'skins' && (
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Standings</div>
            {(settlement.board as Array<{ name: string; you?: boolean; points?: number; total?: number }>).map((row, i) => (
              <div key={i} className={`flex items-center gap-3 rounded-xl border p-3 ${row.you ? 'border-gold bg-gold/10' : 'border-slate-200 bg-slate-50'}`}>
                <div className="w-6 font-serif font-bold text-mute">{i + 1}</div>
                <div className="flex-1 font-bold text-navy">{row.name}</div>
                <div className="font-serif text-lg font-bold">{row.points ?? row.total ?? '—'}</div>
              </div>
            ))}
          </div>
        )}

        {type === 'nassau' && settlement && (
          <div className="space-y-2">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Match Play</div>
            {(['front', 'back', 'total'] as const).map((leg) => {
              const r = settlement[leg] as { winner?: string | null; diff?: number } | undefined;
              if (!r) return null;
              const won = r.winner === myKey;
              const lost = r.winner && r.winner !== myKey;
              const labels = { front: 'FRONT 9', back: 'BACK 9', total: 'TOTAL 18' };
              return (
                <div key={leg} className={`flex items-center gap-3 rounded-xl border p-3 ${won ? 'border-green-300 bg-green-50' : lost ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                  <div className={`flex h-11 w-11 items-center justify-center rounded-full font-serif text-lg font-bold text-white ${won ? 'bg-green-600' : lost ? 'bg-red-500' : 'bg-slate-400'}`}>
                    {won ? 'W' : lost ? 'L' : 'T'}
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] font-bold tracking-wide text-slate-400">{labels[leg]}</div>
                    <div className="font-serif font-bold">
                      {won ? `Won ${r.diff} up` : lost ? `Lost ${r.diff} down` : 'Halved'}
                    </div>
                  </div>
                  <div className={`font-mono text-lg font-bold ${won ? 'text-green-700' : lost ? 'text-red-600' : 'text-slate-400'}`}>
                    {won ? `+$${amount}` : lost ? `-$${amount}` : '$0'}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {type === 'skins' && Array.isArray(settlement?.skinsByHole) && (
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Skins by Hole</div>
            <div className="grid grid-cols-6 gap-1">
              {(settlement.skinsByHole as Array<{ hole: number; winner: string | null; value: number; carried: boolean }>).map((s) => {
                const won = s.winner === myKey;
                const taken = !!s.winner;
                return (
                  <div key={s.hole} className={`rounded p-2 text-center text-xs ${won ? 'bg-gold text-navy' : taken ? 'bg-slate-100' : 'bg-slate-50'}`}>
                    <div className="font-mono text-[9px] text-slate-400">H{s.hole}</div>
                    <div className="font-bold">{taken ? `$${s.value}` : s.carried ? '↻' : '—'}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Settlement</div>
        {opps.length === 0 && (
          <NetSettlementFallback net={net} myKey={myKey} myNet={myNet} members={members} />
        )}
        {opps.map((opp) => {
          const iOwe = myNet < 0 ? -myNet : 0;
          const owedToMe = myNet > 0 ? myNet : 0;
          if (owedToMe === 0 && iOwe === 0) {
            return (
              <div key={opp.id} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                <Avatar member={opp} size={36} />
                <div className="flex-1">
                  <div className="font-semibold text-navy">{opp.name}</div>
                  <div className="text-xs text-slate-500">Tied · No money owed</div>
                </div>
              </div>
            );
          }
          return (
            <div key={opp.id} className={`rounded-xl border p-3 ${owedToMe ? 'border-green-300' : 'border-red-200'}`}>
              <div className="flex items-center gap-3">
                <Avatar member={opp} size={36} />
                <div className="flex-1">
                  <div className="font-bold text-navy">{opp.name}</div>
                  <div className="text-xs text-slate-500">{opp.venmo}</div>
                </div>
                <div className={`font-mono text-xl font-bold ${owedToMe ? 'text-green-700' : 'text-red-600'}`}>
                  {owedToMe ? `+$${owedToMe}` : `-$${iOwe}`}
                </div>
              </div>
              {iOwe > 0 && opp.venmo && (
                <button type="button" onClick={() => venmoPay(opp.venmo)} className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-[#3D95CE] py-2.5 text-xs font-bold text-white">
                  <Wallet size={14} /> OPEN VENMO
                </button>
              )}
            </div>
          );
        })}

        {winnings > 0 && onDonate && !showDonate && (
          <button type="button" onClick={() => setShowDonate(true)} className="w-full rounded-xl border border-gold bg-gold/10 py-3 text-sm font-bold text-navy">
            Donate from winnings
          </button>
        )}
        {showDonate && (
          <div className="flex gap-2">
            {[5, 10, 15, 25].map((pct) => (
              <button key={pct} type="button" onClick={() => donatePct(pct)} className="flex-1 rounded-lg bg-navy py-2 text-xs font-bold text-cream">{pct}%</button>
            ))}
          </div>
        )}

        <p className="text-[11px] leading-relaxed text-slate-500">
          Off-platform settlement. Bag Tag does not process money. Complete payment in Venmo yourself.
        </p>

        {onShare && (
          <button type="button" onClick={onShare} className="w-full rounded-full bg-navy py-3 text-sm font-bold text-cream">Post to Feed</button>
        )}
        <button type="button" onClick={onClose} className="w-full rounded-full border border-slate-200 py-3 text-sm font-semibold text-slate-600">Done</button>
      </div>
    </Sheet>
  );
}

function NetSettlementFallback({
  net,
  myKey,
  myNet,
  members,
}: {
  net: Record<string, number>;
  myKey: string;
  myNet: number;
  members: Record<string, MemberPublic>;
}) {
  const entries = Object.entries(net);
  if (entries.length === 0) {
    return (
      <div className="rounded-xl bg-slate-50 p-3 text-center text-sm text-slate-600">
        Net result: {myNet >= 0 ? `+$${myNet}` : `-$${-myNet}`}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {entries.map(([key, amt]) => {
        const member = members[key];
        const label = member?.name || (key === myKey ? 'You' : key);
        const n = Number(amt) || 0;
        return (
          <div key={key} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
            <span className="font-semibold text-navy">{label}</span>
            <span className={`font-mono font-bold ${n >= 0 ? 'text-green-700' : 'text-red-600'}`}>
              {n >= 0 ? `+$${n}` : `-$${-n}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}
